import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import Modal from "../../components/Modal"
import LoaderDots from "../../components/LoaderDots"
import Icon from "../../Icon"
import { BREAKPOINTS } from "../../GlobalTheme"
import { sendChargerCommand } from "./integrations/chargerCommand"
import { CenteredLoader, CloseButtonContainer, Content, MutedMessage, PanelTitle, Row, RowLabel, RowValue, Section } from "./integrations/integrationStyles"

// The live power sharing state of a Pi2Pi master (VLT-2157): CMD_PS_GET_STATISTICS (msg 56) answers with
// RSP_PS_STATISTICS (msg 57), currents in amps, per-phase values as R, S, T arrays.
const CMD_PS_GET_STATISTICS = 56
const REFRESH_MS = 5000

interface PowerShareStatistics {
  active_chargings?: number[]
  pending_request?: number[]
  sum_chg_load?: number[]
  sum_allocated?: number[]
  n_online_clients?: number
  n_plugged_clients?: number
  n_charging_clients?: number
  i_ext_meter?: number[]
}

const PHASES = ['R', 'S', 'T']

const Layout = styled.div`
  display: flex;
  width: min(560px, calc(100vw - 48px));
  max-height: calc(100dvh - 48px);

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: calc(100vw - 24px);
  }
`

const Subtitle = styled.p`
  margin: -12px 0px 20px;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.colors.gray_500};
`

const PhaseTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};

  th, td {
    padding: 10px 0px 10px 12px;
    text-align: right;
    white-space: nowrap;
  }

  th:first-child, td:first-child {
    padding-left: 0px;
    text-align: left;
    white-space: normal;
  }

  th {
    font-size: 12px;
    font-weight: 700;
    color: ${props => props.theme.colors.gray_500};
  }

  td {
    font-weight: 700;
    border-top: 1px solid ${props => props.theme.colors.gray_200};
  }

  td:first-child {
    font-weight: 600;
  }
`

const HighlightRow = styled.tr`
  td:not(:first-child) {
    font-size: 18px;
    color: ${props => props.theme.colors.primary};
  }
`

export default function PowerShareStatisticsModal({ chargerId, onClose }: { chargerId: string, onClose: () => void }) {
  const { t, i18n } = useTranslation()

  const [statistics, setStatistics] = useState<PowerShareStatistics | null>()
  const [lastUpdate, setLastUpdate] = useState<Date>()

  // One request at a time and the next one 5 s after the answer, never on a fixed beat: the websocket
  // service drops the earlier of two overlapping commands to the same charger. Closing stops it.
  useEffect(() => {
    let isOpen = true
    let timer: ReturnType<typeof setTimeout>

    async function refresh() {
      try {
        const reply = await sendChargerCommand<{ statistics?: PowerShareStatistics }>(chargerId, { msg_type: CMD_PS_GET_STATISTICS }, true)
        if (!isOpen) return
        if (reply?.statistics) {
          setStatistics(reply.statistics)
          setLastUpdate(new Date())
        } else {
          setStatistics(current => current ?? null)
        }
      } catch {
        if (!isOpen) return
        setStatistics(current => current ?? null)
      }
      timer = setTimeout(refresh, REFRESH_MS)
    }

    refresh()
    return () => {
      isOpen = false
      clearTimeout(timer)
    }
  }, [chargerId])

  const amps = (value?: number) => value == null ? '-' : `${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(value)} A`
  const count = (value?: number) => value == null ? '-' : String(value)
  const phases = (values?: number[]) => PHASES.map((_, index) => values?.[index])

  return (
    <Modal onEscape={onClose}>
      <Layout role="dialog" aria-modal="true" aria-label={t('charger_powerShareStatistics')}>
        <Content>
          <CloseButtonContainer>
            <Icon path="general.x" width="20px" height="20px" onClick={onClose} />
          </CloseButtonContainer>
          <PanelTitle>{t('charger_powerShareStatistics')}</PanelTitle>
          <Subtitle>
            {t('charger_updatesEvery5Seconds')}
            {lastUpdate && ` · ${lastUpdate.toLocaleTimeString(i18n.language)}`}
          </Subtitle>

          {statistics === undefined && <CenteredLoader><LoaderDots /></CenteredLoader>}
          {statistics === null && <Section><MutedMessage>{t('charger_couldntLoadThePowerShareStatistics')}</MutedMessage></Section>}
          {statistics && <>
            <Section>
              <PhaseTable>
                <thead>
                  <tr>
                    <th>{t('charger_phase')}</th>
                    {PHASES.map(phase => <th key={phase}>{phase}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <HighlightRow>
                    <td>{t('charger_totalChargingCurrent')}</td>
                    {phases(statistics.sum_chg_load).map((value, index) => <td key={index}>{amps(value)}</td>)}
                  </HighlightRow>
                  <tr>
                    <td>{t('charger_allocatedCurrent')}</td>
                    {phases(statistics.sum_allocated).map((value, index) => <td key={index}>{amps(value)}</td>)}
                  </tr>
                  {statistics.i_ext_meter && (
                    <tr>
                      <td>{t('charger_externalMeterCurrent')}</td>
                      {phases(statistics.i_ext_meter).map((value, index) => <td key={index}>{amps(value)}</td>)}
                    </tr>
                  )}
                  <tr>
                    <td>{t('charger_activeChargingSessions')}</td>
                    {phases(statistics.active_chargings).map((value, index) => <td key={index}>{count(value)}</td>)}
                  </tr>
                  <tr>
                    <td>{t('charger_waitingChargingSessions')}</td>
                    {phases(statistics.pending_request).map((value, index) => <td key={index}>{count(value)}</td>)}
                  </tr>
                </tbody>
              </PhaseTable>
            </Section>
            <Section>
              <Row>
                <RowLabel>{t('charger_onlineClients')}</RowLabel>
                <RowValue>{count(statistics.n_online_clients)}</RowValue>
              </Row>
              <Row>
                <RowLabel>{t('charger_pluggedInClients')}</RowLabel>
                <RowValue>{count(statistics.n_plugged_clients)}</RowValue>
              </Row>
              <Row>
                <RowLabel>{t('charger_chargingClients')}</RowLabel>
                <RowValue>{count(statistics.n_charging_clients)}</RowValue>
              </Row>
            </Section>
          </>}
        </Content>
      </Layout>
    </Modal>
  )
}
